import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  FaArrowLeft,
  FaArrowRight,
  FaBolt,
  FaBrain,
  FaBullhorn,
  FaBookOpen,
  FaBullseye,
  FaCalculator,
  FaChartLine,
  FaCheck,
  FaClipboardCheck,
  FaComments,
  FaCode,
  FaCubes,
  FaDesktop,
  FaEnvelope,
  FaFacebook,
  FaGlobe,
  FaHandshake,
  FaInstagram,
  FaLayerGroup,
  FaLinkedin,
  FaLightbulb,
  FaMousePointer,
  FaPalette,
  FaPenNib,
  FaPhoneAlt,
  FaRoute,
  FaRobot,
  FaSearch,
  FaShareAlt,
  FaShoppingCart,
  FaMoon,
  FaSun,
  FaUserCheck,
  FaWhatsapp,
} from 'react-icons/fa';
import { ODOO_SERVICE, SUPPORTING_SERVICES } from './data/services.js';
import { COMPANY, getAboutMission } from './data/company.js';
import { OdooDashboardArt } from './components/OdooDashboardArt.jsx';
import { OdooModuleMap } from './components/OdooModuleMap.jsx';
import { useTheme } from './theme.jsx';
import { submitContact } from './lib/contact.js';

const media = {
  odoo: '/media/corporate-team.jpg',
  analytics: '/media/analytics-tablet.jpg',
  creative: '/media/creative-studio.jpg',
  social: '/media/social-marketing.jpg',
  web: '/media/web-workspace.jpg',
};

const pathMedia = {
  odoo: [
    {
      src: '/media/generated/odoo-erp-collaboration-v2.jpg',
      path: 'odoo',
      kicker: 'ERP cockpit',
      title: 'Live operations dashboard',
      meta: 'CRM, sales, finance, inventory',
      chips: ['CRM', 'Sales', 'Inventory', 'Accounting'],
      alt: 'UAE business team reviewing a connected ERP operations dashboard together',
      focus: '54% 50%',
    },
    {
      src: '/media/generated/odoo-inventory-workflow-v2.jpg',
      path: 'odoo',
      kicker: 'Inventory flow',
      title: 'Stock and fulfillment visibility',
      meta: 'Warehouse, purchase, sales, delivery',
      chips: ['Purchase', 'Stock', 'Delivery', 'POS'],
      alt: 'UAE warehouse professionals reviewing inventory and fulfillment information on a tablet',
      focus: '50% 48%',
    },
    {
      src: '/media/generated/odoo-crm-operations-v2.jpg',
      path: 'odoo',
      kicker: 'CRM pipeline',
      title: 'Lead to invoice tracking',
      meta: 'Pipeline, quotation, project, invoice',
      chips: ['Leads', 'Quotes', 'Projects', 'Invoices'],
      alt: 'UAE customer operations team reviewing sales pipeline performance together',
      focus: '52% 50%',
    },
    {
      src: '/media/generated/odoo-integrated-workflows-v2.jpg',
      path: 'odoo',
      kicker: 'Connected workflows',
      title: 'One operating system across teams',
      meta: 'Finance, sales, purchase, projects',
      chips: ['Finance', 'Sales', 'Purchase', 'Projects'],
      alt: 'UAE business team aligning connected finance sales purchase and project workflows',
      focus: '50% 50%',
    },
  ],
  agency: [
    {
      src: '/media/growth/growth-strategy-workshop.png',
      path: 'agency',
      kicker: 'Growth strategy',
      title: 'Strategy shaped with the team',
      meta: 'Positioning, planning, priorities, execution',
      chips: ['Position', 'Plan', 'Prioritize', 'Launch'],
      alt: 'Business team developing a growth strategy together on a glass planning board',
      focus: '54% 50%',
    },
    {
      src: '/media/growth/growth-campaign-planning.png',
      path: 'agency',
      kicker: 'Campaign planning',
      title: 'Ideas mapped into action',
      meta: 'Offers, content, channels, conversion',
      chips: ['Offer', 'Content', 'Channels', 'Convert'],
      alt: 'Marketing strategist mapping a campaign flow on a glass board with notes',
      focus: '52% 48%',
    },
    {
      src: '/media/growth/growth-analytics-collaboration.png',
      path: 'agency',
      kicker: 'Marketing analytics',
      title: 'Performance reviewed together',
      meta: 'Traffic, engagement, leads, conversion',
      chips: ['Traffic', 'Engage', 'Leads', 'Convert'],
      alt: 'Team collaborating over marketing performance charts and analytics reports',
      focus: '50% 55%',
    },
    {
      src: '/media/growth/growth-performance.png',
      path: 'agency',
      kicker: 'Growth reporting',
      title: 'Growth signals made visible',
      meta: 'Momentum, insights, decisions, next moves',
      chips: ['Measure', 'Learn', 'Decide', 'Improve'],
      alt: 'Business leader reviewing an upward growth chart on a digital tablet',
      focus: '50% 46%',
    },
    {
      src: '/media/growth/growth-budget-roi.png',
      path: 'agency',
      kicker: 'Campaign ROI',
      title: 'Investment tied to outcomes',
      meta: 'Budget, media spend, returns, optimization',
      chips: ['Budget', 'Spend', 'Return', 'Optimize'],
      alt: 'Marketing budget and return on investment analysis with a calculator, laptop, and reports',
      focus: '55% 52%',
    },
  ],
  operations: [
    {
      src: '/media/generated/accounting-client-review-v2.jpg',
      path: 'operations',
      kicker: 'Accounting desk',
      title: 'Monthly reporting support',
      meta: 'Bookkeeping, payroll, VAT, dashboards',
      chips: ['Books', 'Payroll', 'VAT', 'Reports'],
      alt: 'UAE accountant and business owner reviewing reports and a financial dashboard together',
      focus: '50% 52%',
    },
    {
      src: '/media/generated/accounting-reconciliation-v2.jpg',
      path: 'operations',
      kicker: 'Reconciliation',
      title: 'Books kept accurate and current',
      meta: 'Bank matching, invoices, ledgers, review',
      chips: ['Banks', 'Invoices', 'Ledgers', 'Review'],
      alt: 'Professional accountant reconciling financial reports with a calculator and laptop',
      focus: '50% 48%',
    },
    {
      src: '/media/generated/accounting-vat-payroll-v2.jpg',
      path: 'operations',
      kicker: 'Compliance rhythm',
      title: 'VAT and payroll kept on track',
      meta: 'Filings, approvals, payroll, controls',
      chips: ['VAT', 'Payroll', 'Approvals', 'Controls'],
      alt: 'UAE finance professionals reviewing VAT and payroll documents together',
      focus: '50% 50%',
    },
    {
      src: '/media/generated/accounting-management-reporting-v2.jpg',
      path: 'operations',
      kicker: 'Management reporting',
      title: 'Clear numbers for better decisions',
      meta: 'Cash flow, performance, trends, actions',
      chips: ['Cash flow', 'Performance', 'Trends', 'Actions'],
      alt: 'Finance advisor presenting management reporting and cash flow performance to a business owner',
      focus: '50% 50%',
    },
  ],
};

const serviceIcon = {
  odoo: FaCubes,
  accounting: FaCalculator,
  apps: FaCode,
  ai: FaBrain,
  branding: FaPalette,
  web: FaDesktop,
  marketing: FaBullhorn,
};

const allServices = [ODOO_SERVICE, ...SUPPORTING_SERVICES];
const serviceById = Object.fromEntries(allServices.map((service) => [service.id, service]));

const serviceRouteMap = {
  odoo: 'odoo',
  apps: 'odoo',
  ai: 'odoo',
  branding: 'agency',
  web: 'agency',
  marketing: 'agency',
  accounting: 'operations',
};

const orbitRadii = [178, 178, 178, 178, 178, 178, 178];
const orbitRingRadii = [78, 112, 146, 180, 214];
const orbitAngles = [-90, -38, 12, 66, 122, 180, 210];

const SITE_URL = 'https://www.360digitalgurus.com';
const BASE_PATH = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');

const routePaths = {
  gateway: '/',
  odoo: '/odoo-erp-apps',
  agency: '/brand-web-marketing',
  operations: '/accounting-as-a-service',
};

const routePathAliases = {
  '/': 'gateway',
  '/gateway': 'gateway',
  '/odoo': 'odoo',
  '/odoo-erp': 'odoo',
  '/odoo-erp-apps': 'odoo',
  '/brand': 'agency',
  '/brand-web': 'agency',
  '/brand-web-marketing': 'agency',
  '/agency': 'agency',
  '/ai': 'operations',
  '/ai-accounting': 'operations',
  '/ai-agents-accounting': 'operations',
  '/accounting': 'operations',
  '/accounting-as-a-service': 'operations',
  '/operations': 'operations',
};

const paths = {
  odoo: {
    id: 'odoo',
    label: 'Business Systems',
    nav: 'Systems',
    eyebrow: 'odoo partner / ERP, apps and AI',
    title: 'odoo ERP, custom apps, and AI automation for cleaner operations.',
    shortTitle: 'One ERP core. Apps and AI around it.',
    intro:
      'As an odoo partner, 360 Digital Gurus implements ERP, custom modules, portals, companion apps, integrations, migration, training, and AI automation around one connected operating system.',
    image: media.odoo,
    secondaryImage: media.analytics,
    accent: '#714B67',
    soft: '#f4eaf1',
    dark: '#22111d',
    serviceIds: ['odoo', 'apps', 'ai'],
    outcomes: [
      'odoo discovery, module selection, and an implementation roadmap your team can follow',
      'Custom odoo modules, portals, companion apps, workflows, and integrations around the ERP core',
      'AI agents and automation flows connected to real ERP, approval, document, and reporting processes',
    ],
    process: [
      ['01', 'Discover', 'Map departments, current tools, approval flows, and reporting needs.'],
      ['02', 'Configure', 'Set up the right odoo modules, workflows, integrations, data, and permissions.'],
      ['03', 'Adopt', 'Train users, document processes, and support the team after launch.'],
    ],
  },
  agency: {
    id: 'agency',
    label: 'Brand Growth',
    nav: 'Growth',
    eyebrow: 'Branding / Web / E-commerce / Marketing',
    title: 'Brand, web, e-commerce, and marketing that turn attention into demand.',
    shortTitle: 'Brand growth that gets chosen.',
    intro:
      'Brand strategy, re-branding, web design, e-commerce, content creation, digital marketing, and social media campaigns that make your offer clearer, easier to trust, and easier to choose.',
    image: media.creative,
    secondaryImage: media.social,
    accent: '#E42613',
    soft: '#fff0ee',
    dark: '#310d08',
    serviceIds: ['branding', 'web', 'marketing'],
    outcomes: [
      'Brand strategy and re-branding systems your team can use across every touchpoint',
      'SEO-ready websites, e-commerce journeys, landing pages, and content that make the offer clear',
      'Digital marketing and social media campaigns with practical reporting loops',
    ],
    process: [
      ['01', 'Position', 'Clarify the audience, promise, message, tone, and campaign direction.'],
      ['02', 'Create', 'Design identity assets, website systems, content frames, and campaign material.'],
      ['03', 'Launch', 'Publish, promote, measure, and improve across digital channels.'],
    ],
  },
  operations: {
    id: 'operations',
    label: 'Accounting',
    nav: 'Accounting',
    eyebrow: 'Accounting / VAT / Payroll / Reporting',
    title: 'Accounting as a Service for cleaner finance control.',
    shortTitle: 'Reliable accounting support around your business.',
    intro:
      'Bookkeeping, VAT, payroll coordination, reconciliations, and monthly reporting support for businesses that need reliable finance execution around ERP, operations, and growth.',
    image: media.analytics,
    secondaryImage: media.analytics,
    accent: '#38AECC',
    soft: '#eaf8fc',
    dark: '#071f29',
    serviceIds: ['accounting'],
    outcomes: [
      'Bookkeeping and reconciliations kept in a clear monthly rhythm',
      'VAT, payroll coordination, and reporting support for UAE operations',
      'Finance routines and management visibility that help decisions move with confidence',
    ],
    process: [
      ['01', 'Organize', 'Map bookkeeping, VAT, payroll, reconciliations, documents, and monthly reporting needs.'],
      ['02', 'Operate', 'Run the recurring finance support rhythm with clear ownership and documented handoffs.'],
      ['03', 'Report', 'Deliver management-ready reports, review points, and next-step visibility each month.'],
    ],
  },
};

const routeLabels = {
  gateway: 'Home',
  odoo: 'Systems',
  agency: 'Growth',
  operations: 'Accounting',
};

const gatewayTabs = [
  {
    route: 'odoo',
    line1: 'Systems',
    line2: '',
    tag: 'Systems',
    summary: 'ERP implementation, custom modules, portals, AI workflows, and integrations.',
    services: ['odoo ERP', 'Tailor-made apps', 'AI agents'],
    color: '#714B67',
    title: 'Open odoo ERP and tailor-made apps',
  },
  {
    route: 'agency',
    line1: 'Growth',
    line2: '',
    tag: 'Growth',
    summary: 'Brand identity, websites, content, social media, SEO, and campaigns.',
    services: ['Branding', 'Web design', 'Marketing'],
    color: '#E42613',
    title: 'Open brand, web, and marketing',
  },
  {
    route: 'operations',
    line1: 'Accounting',
    line2: '',
    tag: 'Finance',
    summary: 'Bookkeeping, VAT, payroll, reconciliations, and monthly reporting.',
    services: ['Accounting'],
    color: '#38AECC',
    title: 'Open accounting as a service',
  },
];

const EMAIL_PRIMARY = 'info@360digitalgurus.com';
const PHONE_PRIMARY = '+971 58 600 5045';
const WHATSAPP_NUMBER = '971586005045';
const WHATSAPP_MESSAGE = 'Hey there, I would like to book a consultation with 360 Digital Gurus.';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;
const LINKEDIN_URL = 'https://www.linkedin.com/company/360digitalgurus/';
const FACEBOOK_URL = 'https://www.facebook.com/360digitalgurus';
const INSTAGRAM_URL =
  'https://www.instagram.com/360digitalgurus?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==';

const COUNTRY_OPTIONS = [
  'UAE',
  'Saudi Arabia',
  'Qatar',
  'Kuwait',
  'Bahrain',
  'Oman',
  'United States',
  'United Kingdom',
  'India',
  'Pakistan',
  'Egypt',
  'Jordan',
  'Lebanon',
  'Turkey',
  'Germany',
  'France',
  'Spain',
  'Italy',
  'Netherlands',
  'Canada',
  'Australia',
  'Singapore',
  'South Africa',
];

const IMPLEMENTATION_PHASES = [
  ['01', 'Discovery', 'Workshops and current-state mapping to align on goals, scope, and success metrics.', '#38AECC'],
  ['02', 'Blueprint', 'Module selection, data architecture, integrations, and a clear change-management plan.', '#0047AB'],
  ['03', 'Configure', 'Sprint-based odoo setup with UAE VAT, WPS, and FTA considerations built in.', '#714B67'],
  ['04', 'Migrate', 'Historical data migration, validation, and reconciliation before go-live.', '#E42613'],
  ['05', 'Go-live', 'Cut-over support, training, and a dedicated team on standby in the UAE.', '#38AECC'],
  ['06', 'Operate', 'Ongoing optimization, module rollouts, custom apps, and AI on your odoo data.', '#0047AB'],
];

const APPROACH_BADGES = [
  ['Collaborative partnership', FaHandshake],
  ['Client-centric focus', FaBullseye],
  ['Innovative solutions', FaLightbulb],
  ['Global delivery', FaGlobe],
];

const AFTER_LAUNCH_OUTCOMES = [
  [
    'Clear ownership',
    'Roles, decisions, and next actions are documented so work does not disappear after handover.',
    FaUserCheck,
    '#38AECC',
  ],
  ['Working setup', 'Your team receives a usable system, not a theoretical recommendation deck.', FaClipboardCheck, '#017E84'],
  [
    'Next roadmap',
    'Post-launch improvements are prioritized so automation, reporting, and adoption keep moving.',
    FaRoute,
    '#E42613',
  ],
];

const MARKETING_MOVES = [
  [
    'Campaign idea',
    'One clear concept translated into posts, ads, landing pages, and launch moments.',
    FaChartLine,
    '#38AECC',
  ],
  ['Search content', 'SEO pages, articles, and landing copy that make the right buyers find you.', FaSearch, '#0047AB'],
  ['E-commerce journeys', 'Product pages, offer flow, and content direction shaped for clearer buying decisions.', FaShoppingCart, '#E42613'],
  ['Social storytelling', 'Posts, reels, stories, community care, and platform-specific publishing rhythm.', FaComments, '#714B67'],
];

const CREATIVE_ENGINE_STEPS = [
  [
    'Brand spark',
    'Positioning, voice, visual direction, and reusable identity rules.',
    FaPalette,
    '#714B67',
  ],
  [
    'Web moment',
    'Landing pages, e-commerce paths, SEO copy, and conversion structure.',
    FaDesktop,
    '#0047AB',
  ],
  [
    'Social pulse',
    'Posts, reels, community rhythm, paid campaigns, and launch content.',
    FaShareAlt,
    '#E42613',
  ],
  [
    'Growth readout',
    'Search, analytics, engagement signals, and next-move reporting.',
    FaChartLine,
    '#38AECC',
  ],
];

const CLIENT_WORK = [
  {
    title: 'Omar Hamdan',
    category: 'Personal brand',
    images: [
      {
        src: '/media/client-work/aman-hamdan-group-full.jpg',
        alt: 'Omar Hamdan grooming and product content collage with salon service visuals',
        focus: '50% 50%',
        fit: 'contain',
      },
      {
        src: '/media/client-work/omar-hamdan-portrait.jpg',
        alt: 'Omar Hamdan holding an Omar Gents Salon cup in a branded black-and-white portrait',
        focus: '50% 30%',
      },
      {
        src: '/media/client-work/omar-gents-service.jpg',
        alt: 'Omar Gents Salon branded cup and water served on a tray',
        focus: '50% 38%',
      },
    ],
    summary:
      'We partnered with Omar Hamdan to build a cohesive personal brand presence with a defined brand voice, content pillars, a monthly calendar, and platform-adapted content tracked against clear KPIs.',
    tags: ['Brand voice', 'Content pillars', 'KPI tracking'],
    color: '#38AECC',
    background: '#262626',
  },
  {
    title: 'Elvy by Savora',
    category: 'Brand',
    images: [
      {
        src: '/media/client-work/elly-product-content.webp',
        alt: 'Elvy blueberry syrup bottle photographed with a finished drink in an outdoor product content setup',
        focus: '50% 52%',
      },
      {
        src: '/media/client-work/elvy-pool-product.jpg',
        alt: 'Elvy blueberry syrup bottle presented on a floating ring in a swimming pool',
        focus: '50% 52%',
      },
      {
        src: '/media/client-work/elvy-blueberry-cocktail.jpg',
        alt: 'Elvy blueberry syrup bottle and a finished cocktail in an outdoor product scene',
        focus: '50% 58%',
      },
      {
        src: '/media/client-work/elvy-mixology.jpg',
        alt: 'Mixologist preparing a drink with an Elvy passion fruit syrup bottle',
        focus: '50% 58%',
      },
    ],
    summary:
      'For Elvy by Savora, we developed a marketing strategy and ongoing content built around consistent brand messaging, visual guidelines, product storytelling, and seasonal campaigns.',
    tags: ['Brand strategy', 'Product storytelling', 'Seasonal campaigns'],
    color: '#714B67',
  },
  {
    title: 'Plus961',
    category: 'Media platform',
    images: [
      {
        src: '/media/client-work/automotive-showroom-content.webp',
        alt: 'Automotive editorial content featuring luxury cars in a premium showroom',
        focus: '50% 72%',
      },
      {
        src: '/media/client-work/automotive-workshop-porsche.jpg',
        alt: 'Automotive editorial content featuring classic and modern Porsche vehicles in a workshop',
        focus: '50% 58%',
      },
      {
        src: '/media/client-work/automotive-service-tools.jpg',
        alt: 'Automotive editorial content featuring service tools and performance cars',
        focus: '44% 58%',
      },
    ],
    summary:
      "We worked with Plus961 on a data-informed editorial strategy, a structured content calendar balancing timely and evergreen topics, and multi-format content built for cross-platform reach and engagement.",
    tags: ['Editorial strategy', 'Content calendar', 'Cross-platform reach'],
    color: '#E42613',
  },
];

const BRAND_RESOURCES = [
  [
    'Brand strategy',
    'Positioning, audience clarity, promise, and market differentiation before visual work begins.',
    FaLayerGroup,
    '#0047AB',
  ],
  ['Identity system', 'Logo usage, color, typography, icon rules, and layouts that keep the brand recognizable.', FaPalette, '#38AECC'],
  ['Messaging kit', 'Core narrative, service language, offer framing, and copy direction for sales and web teams.', FaPenNib, '#E42613'],
  ['Guideline assets', 'Practical brand resources your team can reuse across web, social, documents, and campaigns.', FaBookOpen, '#017E84'],
];

const WHO_WE_ARE_POINTS = [
  [
    'Execution partner',
    'We turn strategy into usable systems, campaigns, workflows, and support.',
    FaHandshake,
    '#0047AB',
  ],
  ['Dubai, UAE presence', 'Regional business context with global delivery for growing teams.', FaGlobe, '#38AECC'],
  ['Client-centric delivery', 'Solutions are shaped around real workflows, constraints, and adoption needs.', FaBullseye, '#E42613'],
  ['Built for adoption', 'Training, documentation, and improvement support stay part of the work.', FaUserCheck, '#714B67'],
];

const OPERATIONS_DETAILS = [
  [
    'Bookkeeping with ownership',
    'Recurring entries, supporting documents, reconciliations, and handoffs are organized so the month does not become a scramble.',
    FaCalculator,
    '#38AECC',
  ],
  [
    'VAT and compliance rhythm',
    'VAT support, review points, and finance calendars help UAE operations stay clean, documented, and ready for reporting.',
    FaClipboardCheck,
    '#E42613',
  ],
  [
    'Payroll coordination',
    'Payroll inputs, approvals, and coordination stay visible so leadership knows what is pending and what is complete.',
    FaUserCheck,
    '#0047AB',
  ],
  [
    'Management reporting',
    'Monthly reports turn bookkeeping into decision-ready numbers for owners, managers, and finance teams.',
    FaChartLine,
    '#017E84',
  ],
];

const seoByRoute = {
  gateway: {
    title: '360 Digital Gurus | Business Systems, Growth and Accounting',
    description:
      '360 Digital Gurus connects odoo ERP, custom apps, AI automation, brand growth, web, marketing, and accounting support for UAE and global teams.',
  },
  odoo: {
    title: 'Business Systems | odoo ERP, Apps and AI | 360 Digital Gurus',
    description:
      'Work with an odoo partner for ERP implementation, custom modules, portals, companion apps, AI automation, integrations, migration, training, and UAE VAT/WPS-ready workflows.',
  },
  agency: {
    title: 'Growth | Brand, Web and Digital Marketing | 360 Digital Gurus',
    description:
      'Build brand strategy, re-branding, web design, e-commerce, SEO content, social media, and campaigns that turn attention into qualified demand.',
  },
  operations: {
    title: 'Accounting as a Service | 360 Digital Gurus',
    description:
      'Get bookkeeping, VAT, payroll coordination, reconciliations, and monthly management reporting support for UAE business operations.',
  },
};

function setMeta(name, content, attribute = 'name') {
  let tag = document.querySelector(`meta[${attribute}="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function setLink(rel, href) {
  let tag = document.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
}

function normalizeRoutePath(pathname = '/') {
  let path = pathname || '/';
  if (BASE_PATH && path.startsWith(`${BASE_PATH}/`)) {
    path = path.slice(BASE_PATH.length) || '/';
  }
  path = path.replace(/\/+$/, '') || '/';
  return path;
}

function getRoutePath(route) {
  const path = routePaths[route] || routePaths.gateway;
  return BASE_PATH ? `${BASE_PATH}${path}`.replace(/\/{2,}/g, '/') : path;
}

function getCanonicalUrl(route) {
  return `${SITE_URL}${routePaths[route] || routePaths.gateway}`;
}

function usePageSeo(route) {
  useEffect(() => {
    const seo = seoByRoute[route] || seoByRoute.gateway;
    const canonicalUrl = getCanonicalUrl(route);
    document.title = seo.title;
    setMeta('description', seo.description);
    setMeta('og:title', seo.title, 'property');
    setMeta('og:description', seo.description, 'property');
    setMeta('og:url', canonicalUrl, 'property');
    setMeta('twitter:title', seo.title);
    setMeta('twitter:description', seo.description);
    setLink('canonical', canonicalUrl);
  }, [route]);
}

function publicCopy(text = '') {
  return text.replace(/odoo/gi, 'odoo');
}

function getRouteFromHash() {
  const value = window.location.hash.replace('#/', '').replace('#', '');
  return routeLabels[value] ? value : null;
}

function getRouteFromLocation() {
  const hashRoute = getRouteFromHash();
  if (hashRoute) return hashRoute;
  const pathRoute = routePathAliases[normalizeRoutePath(window.location.pathname)];
  if (pathRoute) return pathRoute;
  return 'gateway';
}

function scrollContact() {
  const section = document.getElementById('contact');
  if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function setRoute(route, options = {}) {
  const nextPath = getRoutePath(route);
  const samePath = normalizeRoutePath(window.location.pathname) === normalizeRoutePath(nextPath) && !window.location.hash;
  if (samePath) {
    if (options.contact) scrollContact();
    else window.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }
  window.history.pushState({ route }, '', nextPath);
  window.dispatchEvent(new PopStateEvent('popstate'));
  if (options.contact) window.setTimeout(scrollContact, 80);
}

function useRoute() {
  const [route, updateRoute] = useState(() => getRouteFromLocation());

  useLayoutEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

    const onRouteChange = () => {
      updateRoute(getRouteFromLocation());
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    };
    window.addEventListener('popstate', onRouteChange);
    window.addEventListener('hashchange', onRouteChange);
    return () => {
      window.removeEventListener('popstate', onRouteChange);
      window.removeEventListener('hashchange', onRouteChange);
    };
  }, []);

  return route;
}

function CurtainLoader({ route }) {
  const [visible, setVisible] = useState(true);
  const [opening, setOpening] = useState(false);
  const firstRun = useRef(true);

  useEffect(() => {
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      setVisible(false);
      return undefined;
    }

    const initial = firstRun.current;
    firstRun.current = false;
    setVisible(true);
    setOpening(false);

    const hold = initial ? 520 : 180;
    const openTimer = window.setTimeout(() => setOpening(true), hold);
    const removeTimer = window.setTimeout(() => setVisible(false), hold + 760);

    return () => {
      window.clearTimeout(openTimer);
      window.clearTimeout(removeTimer);
    };
  }, [route]);

  if (!visible) return null;

  return (
    <div className={`curtain-loader${opening ? ' is-opening' : ''}`} aria-hidden="true">
      <span className="curtain-loader__panel curtain-loader__panel--left" />
      <span className="curtain-loader__panel curtain-loader__panel--right" />
      <span className="curtain-loader__beam" />
      <span className="curtain-loader__brand">
        <img src="/360dg-logo-white.svg" alt="" />
        <strong>360 Digital Gurus</strong>
      </span>
    </div>
  );
}

function ScrollLifeEffects({ route }) {
  useEffect(() => {
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const sections = [
      ...new Set([
        ...document.querySelectorAll(
          [
            'main > section',
            '.path-page > section',
            '.v2-white-section',
            '.site-footer',
          ].join(', '),
        ),
      ]),
    ];
    const itemSelector = [
      '.home-hero__copy > *',
      '.gateway-orbit',
      '.path-hero__media',
      '.path-hero__copy > *',
      '.section-heading > *',
      '.v2-section-head > *',
      '.creative-engine-head > *',
      '.client-work-head > *',
      '.who-copy > *',
      '.who-identity',
      '.who-points > article',
      '.path-grid > .path-door',
      '.path-promise > *',
      '.outcome-list > p',
      '.odoo-proof-grid > article',
      '.v2-process-step',
      '.v2-process-strip',
      '.v2-outcome-points > button',
      '.v2-growth-feature > *',
      '.v2-growth-cards > button',
      '.creative-engine__stage',
      '.creative-engine__steps > button',
      '.client-work-card',
      '.v2-brand-resource > *',
      '.v2-brand-grid > button',
      '.operations-lab__copy > *',
      '.operations-lab__visuals',
      '.operations-detail-grid > article',
      '.path-service-grid > .service-detail',
      '.path-process > article',
      '.path-cta > *',
      '.home-contact__copy > *',
      '.contact-form',
      '.odoo-map-block',
      '.site-footer > *',
    ].join(', ');

    const sectionItems = new Map();

    sections.forEach((section) => {
      const items = [...new Set([...section.querySelectorAll(itemSelector)])].filter((item) => item !== section);
      section.classList.add('scroll-life-section');
      section.classList.remove('is-visible');

      items.forEach((item, index) => {
        const direction = index % 3;
        item.classList.add('scroll-life-item');
        item.style.setProperty('--scroll-delay', `${Math.min(index * 48, 300)}ms`);
        item.style.setProperty('--scroll-x', direction === 0 ? '-14px' : direction === 1 ? '0px' : '14px');
        item.style.setProperty('--scroll-y', `${22 + (index % 2) * 8}px`);
      });

      sectionItems.set(section, items);
    });

    if (reduceMotion) {
      sections.forEach((section) => section.classList.add('is-visible'));
      return () => {
        sections.forEach((section) => {
          section.classList.remove('scroll-life-section', 'is-visible');
          (sectionItems.get(section) || []).forEach((item) => {
            item.classList.remove('scroll-life-item');
            item.style.removeProperty('--scroll-delay');
            item.style.removeProperty('--scroll-x');
            item.style.removeProperty('--scroll-y');
          });
        });
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.12 },
    );

    sections.forEach((section) => observer.observe(section));

    return () => {
      observer.disconnect();
      sections.forEach((section) => {
        section.classList.remove('scroll-life-section', 'is-visible');
        (sectionItems.get(section) || []).forEach((item) => {
          item.classList.remove('scroll-life-item');
          item.style.removeProperty('--scroll-delay');
          item.style.removeProperty('--scroll-x');
          item.style.removeProperty('--scroll-y');
        });
      });
    };
  }, [route]);

  return null;
}

function Kicker({ children }) {
  return <span className="section-kicker">{children}</span>;
}

function OdooName({ className = '' }) {
  return (
    <span className={`odoo-name ${className}`.trim()} aria-label="odoo">
      <span className="odoo-name__o" aria-hidden="true">
        o
      </span>
      <span className="odoo-name__rest" aria-hidden="true">
        doo
      </span>
    </span>
  );
}

function OdooText({ children }) {
  const text = String(children ?? '');
  return text.split(/(odoo)/gi).map((part, index) =>
    part.toLowerCase() === 'odoo' ? (
      <OdooName key={`${part}-${index}`} />
    ) : (
      <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
    ),
  );
}

function GradientText({ children, variant = 'full' }) {
  return <span className={variant === 'cool' ? 'gradient-text-cool' : 'gradient-text'}>{children}</span>;
}

function Navigation({ route }) {
  const [open, setOpen] = useState(false);
  const { theme, setTheme } = useTheme();

  const go = (target) => {
    setOpen(false);
    setRoute(target);
  };

  const contact = () => {
    setOpen(false);
    setRoute('gateway', { contact: true });
  };

  return (
    <header className="topbar">
      <button className="brand-lockup" onClick={() => go('gateway')} aria-label="Open 360 Digital Gurus gateway">
        <img src="/360dg-logo-white.svg" alt="360 Digital Gurus" />
      </button>
      <button className="menu-toggle" onClick={() => setOpen((value) => !value)} aria-label="Toggle navigation">
        <span />
        <span />
      </button>
      <nav className={open ? 'nav-links nav-links--open' : 'nav-links'} aria-label="Main navigation">
        {Object.entries(routeLabels).map(([key, label]) => (
          <button key={key} className={route === key ? 'is-active' : ''} onClick={() => go(key)}>
            <OdooText>{label}</OdooText>
          </button>
        ))}
        <button onClick={contact}>Contact</button>
      </nav>
      <button
        className="theme-toggle"
        type="button"
        aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        aria-pressed={theme === 'dark'}
        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
      >
        <FaMoon className={theme === 'dark' ? 'is-active' : ''} aria-hidden="true" />
        <FaSun className={theme === 'light' ? 'is-active' : ''} aria-hidden="true" />
      </button>
      <button className="nav-cta" onClick={contact}>
        Book consultation
        <FaArrowRight />
      </button>
    </header>
  );
}

function HeroConnectorLines() {
  const lines = [
    {
      id: 'odoo',
      color: '#714B67',
      path: 'M 500 238 C 420 334 248 342 166 438',
      end: [166, 438],
    },
    {
      id: 'agency',
      color: '#E42613',
      path: 'M 500 238 C 500 310 500 370 500 438',
      end: [500, 438],
    },
    {
      id: 'operations',
      color: '#38AECC',
      path: 'M 500 238 C 580 334 752 342 834 438',
      end: [834, 438],
    },
  ];

  return (
    <svg className="hero-motion-connectors" viewBox="0 0 1000 520" preserveAspectRatio="none" aria-hidden="true">
      {lines.map((line, index) => (
        <g key={line.id} style={{ '--connector-color': line.color, '--connector-delay': `${0.18 + index * 0.16}s` }}>
          <path className="hero-motion-connector hero-motion-connector--glow" d={line.path} />
          <path className="hero-motion-connector" d={line.path} />
          <circle className="hero-motion-connector__dot" cx={line.end[0]} cy={line.end[1]} r="7" />
        </g>
      ))}
    </svg>
  );
}

function HeroMotion() {
  return (
    <div className="hero-motion" aria-label="Choose a 360 Digital Gurus path">
      <div className="hero-motion-video" aria-hidden="true">
        <video autoPlay muted loop playsInline poster="/media/360dg-gateway-still.png">
          <source src="/media/360dg-gateway-loop.webm" type="video/webm" />
          <source src="/media/360dg-gateway-loop.mp4" type="video/mp4" />
        </video>
      </div>
      <HeroConnectorLines />
      <div className="hero-motion-tabs" aria-label="Choose a 360 Digital Gurus path">
        {gatewayTabs.map((tab, index) => (
          <button
            key={tab.route}
            type="button"
            className={`hero-motion-tab hero-motion-tab--${tab.route}`}
            style={{ '--tab-color': tab.color, '--tab-index': index }}
            onClick={() => setRoute(tab.route)}
            aria-label={tab.title}
          >
            <span className="hero-motion-tab__title">
              <OdooText>{tab.line1}</OdooText>
              {tab.line2 && <strong>{tab.line2}</strong>}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function GatewayServiceOrbit() {
  const [activeNode, setActiveNode] = useState('odoo');
  const nodes = useMemo(
    () =>
      allServices.map((service, index) => ({
        ...service,
        route: serviceRouteMap[service.id] || 'gateway',
        angle: orbitAngles[index],
        radius: orbitRadii[index],
        delay: '0s',
        duration: '36s',
      })),
    [],
  );

  return (
    <div className="gateway-orbit" aria-label="360 Digital Gurus service orbit">
      <div className="gateway-orbit__glow" aria-hidden="true" />
      <svg viewBox="0 0 520 520" className="gateway-orbit__svg" aria-hidden="true">
        <defs>
          <linearGradient id="gatewayOrbitRing" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#714B67" />
            <stop offset="45%" stopColor="#38AECC" />
            <stop offset="100%" stopColor="#E42613" />
          </linearGradient>
        </defs>
        <g className="gateway-orbit__rings">
          {orbitRingRadii.map((radius, index) => (
            <circle
              key={radius}
              cx="260"
              cy="260"
              r={radius}
              fill="none"
              stroke={index === 1 ? 'url(#gatewayOrbitRing)' : 'rgba(0, 71, 171, 0.12)'}
              strokeWidth={index === 1 ? 2 : 1}
              strokeDasharray={index % 2 === 0 ? '5 10' : 'none'}
            />
          ))}
        </g>
      </svg>

      <button className="gateway-orbit__mark" type="button" onClick={() => setRoute(serviceRouteMap[activeNode] || 'gateway')}>
        <span className="gateway-orbit__mark-depth" aria-hidden="true" />
        <img src="/360dg-icon.svg" alt="360 Digital Gurus" />
      </button>

      {nodes.map((node) => (
        <button
          key={node.id}
          type="button"
          className={`gateway-orbit-chip${activeNode === node.id ? ' is-active' : ''}`}
          style={{
            '--orbit-angle': `${node.angle}deg`,
            '--orbit-angle-reverse': `${node.angle * -1}deg`,
            '--orbit-angle-final': `${node.angle + 360}deg`,
            '--orbit-angle-final-reverse': `${(node.angle + 360) * -1}deg`,
            '--chip-radius': `${node.radius}px`,
            '--chip-color': node.color,
            '--chip-duration': node.duration,
            animationDelay: node.delay,
          }}
          onMouseEnter={() => setActiveNode(node.id)}
          onFocus={() => setActiveNode(node.id)}
          onClick={() => setRoute(node.route)}
          aria-label={`Open ${node.name}`}
        >
          <span className="gateway-orbit-chip__dot" aria-hidden="true" />
          <span className="gateway-orbit-chip__label">
            <OdooText>{node.name}</OdooText>
          </span>
        </button>
      ))}
    </div>
  );
}

function CategoryVisual({ slide }) {
  return (
    <div className={`category-visual category-visual--${slide.path}`} role="img" aria-label={slide.alt}>
      <div className="category-visual__wash" />
      <div className="category-visual__screen">
        <div className="category-visual__top">
          <span>{slide.kicker}</span>
          <em>{slide.path === 'odoo' ? 'ERP' : slide.path === 'agency' ? 'Growth' : 'Ops'}</em>
        </div>
        <strong>{slide.title}</strong>
        <p>{slide.meta}</p>
        <div className="category-visual__chart" aria-hidden="true">
          <span />
          <span />
          <span />
          <span />
          <span />
        </div>
      </div>
      <div className="category-visual__chips" aria-hidden="true">
        {slide.chips.map((chip) => (
          <span key={chip}>{chip}</span>
        ))}
      </div>
    </div>
  );
}

function ImageRotator({ images = [], className = '', intervalMs = 3000 }) {
  const slides = images.length ? images : [{ src: media.analytics, alt: '360 Digital Gurus digital delivery workspace' }];
  const [activeIndex, setActiveIndex] = useState(0);
  const slideCount = slides.length;

  useEffect(() => {
    setActiveIndex(0);
    if (slideCount <= 1) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % slideCount);
    }, Math.max(500, intervalMs));

    return () => window.clearInterval(timer);
  }, [intervalMs, slideCount]);

  return (
    <div className={`image-rotator ${className}`}>
      {slides.map((image, index) =>
        image.src ? (
          <img
            key={`${image.src}-${index}`}
            className={`image-rotator__item${index === activeIndex ? ' is-active' : ''}`}
            src={image.src}
            alt={image.alt}
            style={{
              ...(image.focus ? { objectPosition: image.focus } : {}),
              ...(image.fit ? { objectFit: image.fit } : {}),
            }}
            loading={index === 0 ? 'eager' : 'lazy'}
            decoding="async"
            aria-hidden={index !== activeIndex}
          />
        ) : (
          <div
            key={`${image.path}-${image.title}-${index}`}
            className={`image-rotator__slide image-rotator__item${index === activeIndex ? ' is-active' : ''}`}
          >
            <CategoryVisual slide={image} />
          </div>
        ),
      )}
    </div>
  );
}

function HomePage() {
  return (
    <main>
      <section className="home-hero">
        <div className="home-hero__copy">
          <Kicker>Empowering business transformation</Kicker>
          <h1>Bring every digital part of your business into motion.</h1>
          <p>
            <OdooText>
              {'360 Digital Gurus connects odoo ERP, custom apps, AI automation, branding, websites, e-commerce, marketing, and accounting support through one accountable team.'}
            </OdooText>
          </p>
        </div>
        <GatewayServiceOrbit />
      </section>

      <section className="paths-section">
        <div className="section-heading">
          <Kicker>Choose your direction</Kicker>
          <h2>Focused where it matters. Connected where your business needs flow.</h2>
          <p>
            Each path has its own service depth, visuals, and delivery logic, while 360 Digital Gurus stays the
            accountable partner behind the full engagement.
          </p>
        </div>
        <div className="path-grid">
          {Object.values(paths).map((path) => (
            <PathDoor key={path.id} path={path} />
          ))}
        </div>
      </section>

      <WhoWeAreSection />

      <HomeContact />
    </main>
  );
}

function WhoWeAreSection() {
  return (
    <section className="who-section">
      <div className="who-layout">
        <div className="who-copy">
          <Kicker>Who we are</Kicker>
          <h2>The team behind the systems, stories, and support.</h2>
          <p>
            <OdooText>
              360 Digital Gurus helps companies connect the operational, creative, and financial parts of their digital
              business without losing accountability between teams.
            </OdooText>
          </p>
          <p>
            <OdooText>{getAboutMission()}</OdooText>
          </p>
        </div>

        <div className="who-identity">
          <div className="who-identity__mark" aria-hidden="true">
            <span>360DG</span>
            <img src="/360dg-logo-white.svg" alt="" />
          </div>
          <div className="who-identity__copy">
            <span className="who-identity__label">{COMPANY.legalName}</span>
            <strong>
              <OdooText>One accountable team across odoo ERP, apps, brand growth, AI automation, and accounting support.</OdooText>
            </strong>
            <em className="who-identity__meta">Dubai, UAE / Global delivery</em>
            <div className="who-metrics" aria-label="360 Digital Gurus delivery profile">
              <span>
                <b>3</b>
                Focused paths
              </span>
              <span>
                <b>7</b>
                Core services
              </span>
              <span>
                <b>1</b>
                One team
              </span>
            </div>
          </div>
        </div>

        <div className="who-points">
          {WHO_WE_ARE_POINTS.map(([title, copy, Icon, color]) => (
            <article key={title} style={{ '--who-color': color }}>
              <i>
                <Icon />
              </i>
              <strong>{title}</strong>
              <span>{copy}</span>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function PathDoor({ path }) {
  return (
    <article className={`path-door path-door--${path.id}`} style={{ '--path-color': path.accent, '--path-soft': path.soft }}>
      <div className="path-door__image">
        {pathMedia[path.id] ? (
          <ImageRotator images={pathMedia[path.id]} />
        ) : (
          <img src={path.image} alt={`${path.label} service preview`} />
        )}
      </div>
      <div className="path-door__content">
        <span>{path.eyebrow}</span>
        <h2>
          <OdooText>{path.shortTitle}</OdooText>
        </h2>
        <p>
          <OdooText>{path.intro}</OdooText>
        </p>
        <div className="path-door__services">
          {path.serviceIds.map((id) => (
            <strong key={id} style={{ '--service-chip-color': serviceById[id].color }}>
              <OdooText>{publicCopy(serviceById[id].name)}</OdooText>
            </strong>
          ))}
        </div>
        <button onClick={() => setRoute(path.id)}>
          Enter this path
          <FaArrowRight />
        </button>
      </div>
    </article>
  );
}

function ServiceDetail({ service }) {
  const Icon = serviceIcon[service.id] || FaBolt;
  const isOdooService = service.id === 'odoo';
  const features = service.features || ['Discovery and direction', 'Execution plan', 'Launch support', 'Ongoing improvement'];

  return (
    <article className={`service-detail${isOdooService ? ' service-detail--odoo' : ''}`} style={{ '--service-color': service.color }}>
      <div className="service-detail__top">
        <span>
          <Icon />
        </span>
        <strong>
          {isOdooService ? (
            <span className="service-detail__odoo-title">
              <img src="/odoo_logo.svg" alt="odoo" />
              ERP Implementation
            </span>
          ) : (
            <OdooText>{publicCopy(service.name)}</OdooText>
          )}
        </strong>
      </div>
      <p>
        <OdooText>{publicCopy(service.description)}</OdooText>
      </p>
      <ul>
        {features.slice(0, 4).map((feature) => (
          <li key={feature}>{feature}</li>
        ))}
      </ul>
    </article>
  );
}

function PathPage({ path }) {
  const services = path.serviceIds.map((id) => serviceById[id]);
  const isOdoo = path.id === 'odoo';

  return (
    <main
      className={`path-page path-page--${path.id}`}
      style={{ '--path-color': path.accent, '--path-soft': path.soft, '--path-dark': path.dark }}
    >
      <section className="path-hero">
        <div className={isOdoo ? 'path-hero__media path-hero__media--odoo-art' : 'path-hero__media'}>
          {isOdoo ? (
            <OdooDashboardArt />
          ) : (
            <>
              <ImageRotator images={pathMedia[path.id] || [{ src: path.image, alt: `${path.label} service preview` }]} />
              <div className="media-badge">
                <FaLayerGroup />
                <span>{services.length} focused service{services.length > 1 ? 's' : ''}</span>
              </div>
            </>
          )}
        </div>
        <div className="path-hero__copy">
          <button className="back-button" onClick={() => setRoute('gateway')}>
            <FaArrowLeft />
            Back to Home
          </button>
          <Kicker>{path.eyebrow}</Kicker>
          <h1>
            <OdooText>{path.title}</OdooText>
          </h1>
          <p>
            <OdooText>{path.intro}</OdooText>
          </p>
          <div className="path-hero__actions">
            <button onClick={() => setRoute('gateway', { contact: true })}>
              Start with this team
              <FaArrowRight />
            </button>
            <button className="button-light" onClick={() => setRoute(path.id === 'odoo' ? 'agency' : 'odoo')}>
              Switch path
              <FaArrowRight />
            </button>
          </div>
        </div>
      </section>

      <PathPromise path={path} />

      {path.id === 'odoo' && <OdooExperience />}
      {path.id === 'agency' && <AgencyExperience />}
      {path.id === 'operations' && <OperationsExperience />}

      <section className="path-services">
        <div className="section-heading">
          <Kicker>Service depth</Kicker>
          <h2>{path.id === 'odoo' ? 'Implementation scope.' : 'What this path contains.'}</h2>
        </div>
        <div
          className={`path-service-grid path-service-grid--count-${services.length}${
            services.length === 1 ? ' path-service-grid--single' : ''
          }`}
        >
          {services.map((service) => (
            <ServiceDetail key={service.id} service={service} />
          ))}
        </div>
      </section>

      {path.id === 'operations' && (
        <section className="path-process-section">
          <div className="section-heading">
            <Kicker>Delivery flow</Kicker>
            <h2>From repeated work to managed operations.</h2>
            <p>
              We identify the operational gaps, build the right support layer, then keep governance and reporting clear.
            </p>
          </div>
          <div className="path-process">
            {path.process.map(([number, title, copy]) => (
              <article key={number}>
                <span>{number}</span>
                <h3>{title}</h3>
                <p>
                  <OdooText>{copy}</OdooText>
                </p>
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="path-cta">
        <h2>
          Ready to discuss <OdooText>{path.label}</OdooText>?
        </h2>
        <p>Start with a focused consultation and move into the path that matches your priority.</p>
        <button onClick={() => setRoute('gateway', { contact: true })}>
          Contact 360DG
          <FaArrowRight />
        </button>
      </section>
    </main>
  );
}

function PathPromise({ path }) {
  return (
    <section className="path-promise">
      <div>
        <Kicker>
          <OdooText>{path.label}</OdooText>
        </Kicker>
        <h2>
          <OdooText>{path.shortTitle}</OdooText>
        </h2>
      </div>
      <div className="outcome-list">
        {path.outcomes.map((outcome) => (
          <p key={outcome}>
            <FaCheck />
            <OdooText>{outcome}</OdooText>
          </p>
        ))}
      </div>
    </section>
  );
}

function WhiteGridSection({ className = '', children }) {
  return <section className={`v2-white-section ${className}`.trim()}>{children}</section>;
}

function V2Eyebrow({ children }) {
  return (
    <span className="v2-eyebrow">
      <span />
      {children}
    </span>
  );
}

function OdooApproachSection() {
  return (
    <WhiteGridSection className="v2-process-section">
      <div className="v2-section-head">
        <V2Eyebrow>Our approach</V2Eyebrow>
        <h2>
          Innovation, expertise, and <GradientText variant="cool">collaboration.</GradientText>
        </h2>
        <p>
          Our delivery approach combines discovery, clean implementation, training, and ongoing improvement so every
          engagement moves from plan to usable outcome.
        </p>
      </div>

      <div className="v2-process-wrap">
        <div className="v2-process-beam" />
        <div className="v2-process-rail">
          {IMPLEMENTATION_PHASES.map(([number, title, copy, color], index) => (
            <article key={number} className="v2-process-step" style={{ '--process-color': color, '--process-delay': `${index * 2}s` }}>
              <span className="v2-process-dot" />
              <div>
                <em>{number}</em>
                <strong>{title}</strong>
                <p>
                  <OdooText>{copy}</OdooText>
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>

      <div className="v2-process-strip">
        <div>
          {APPROACH_BADGES.map(([label, Icon]) => (
            <span key={label}>
              <Icon />
              {label}
            </span>
          ))}
        </div>
        <button type="button" onClick={() => setRoute('gateway', { contact: true })}>
          Plan my project
          <FaArrowRight />
        </button>
      </div>
    </WhiteGridSection>
  );
}

function AfterLaunchSection() {
  return (
    <WhiteGridSection className="v2-outcome-section">
      <div className="v2-section-head v2-section-head--with-action">
        <V2Eyebrow>After launch</V2Eyebrow>
        <h2>
          Built to be used, <GradientText>not just delivered.</GradientText>
        </h2>
        <p>
          Every project should leave your team with clear ownership, a working setup, and a practical path for what
          improves next.
        </p>
        <button type="button" onClick={() => setRoute('gateway', { contact: true })}>
          Start your project
          <FaArrowRight />
        </button>
      </div>
      <div className="v2-outcome-strip v2-outcome-strip--cards">
        <div className="v2-outcome-points">
          {AFTER_LAUNCH_OUTCOMES.map(([title, copy, Icon, color], index) => (
            <button key={title} type="button" style={{ '--outcome-color': color }} onClick={() => setRoute('gateway', { contact: true })}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <i>
                <Icon />
              </i>
              <strong>{title}</strong>
              <em>{copy}</em>
            </button>
          ))}
        </div>
      </div>
    </WhiteGridSection>
  );
}

function DigitalMarketingSection() {
  return (
    <WhiteGridSection className="v2-growth-section">
      <div className="v2-section-head v2-section-head--with-action">
        <V2Eyebrow>Digital marketing & social media</V2Eyebrow>
        <h2>
          Turn attention into <GradientText>qualified demand.</GradientText>
        </h2>
        <p>Campaigns should feel alive: strong visuals, clear offers, platform-ready content, and a reporting rhythm that keeps improving the work.</p>
        <div className="v2-growth-actions">
          <button type="button" onClick={() => setRoute('gateway', { contact: true })}>
            Plan a campaign
            <FaArrowRight />
          </button>
          <button type="button" className="button-light" onClick={() => document.querySelector('.path-services')?.scrollIntoView({ behavior: 'smooth' })}>
            View services
          </button>
        </div>
      </div>
      <div className="v2-growth-feature v2-growth-feature--creative">
        <div className="campaign-loop-board" aria-label="Digital marketing campaign loop">
          <div className="campaign-loop-board__poster" aria-hidden="true">
            <span className="campaign-loop-board__badge">Live campaign</span>
            <strong>
              Hook the eye.
              <br />
              Earn the click.
              <br />
              Prove the result.
            </strong>
            <div className="campaign-loop-board__story">
              <span />
              <span />
              <span />
            </div>
            <div className="campaign-loop-board__metrics">
              <b>+38%</b>
              <em>Qualified demand</em>
            </div>
          </div>
          <div className="campaign-loop-board__stickers" aria-hidden="true">
            {[
              ['Hook', '#E42613'],
              ['Content', '#38AECC'],
              ['Search', '#0047AB'],
              ['Social', '#F25C2E'],
              ['Paid', '#714B67'],
              ['Report', '#017E84'],
            ].map(([label, color], index) => (
              <em key={label} className={`campaign-loop-board__node campaign-loop-board__node--${index + 1}`} style={{ '--node-color': color }}>
                {label}
              </em>
            ))}
          </div>
          <div className="campaign-loop-board__lanes">
            <span style={{ '--lane-color': '#714B67' }}>
              <b>Brand cue</b>
              Promise, look, proof
            </span>
            <span style={{ '--lane-color': '#0047AB' }}>
              <b>Web moment</b>
              Landing, SEO, conversion
            </span>
            <span style={{ '--lane-color': '#E42613' }}>
              <b>Social spark</b>
              Posts, reels, paid, report
            </span>
          </div>
        </div>

        <div className="v2-growth-cards">
          {MARKETING_MOVES.map(([title, copy, Icon, color]) => (
            <button key={title} type="button" style={{ '--growth-color': color }} onClick={() => setRoute('gateway', { contact: true })}>
              <i>
                <Icon />
              </i>
              <strong>{title}</strong>
              <span>{copy}</span>
            </button>
          ))}
        </div>
      </div>
    </WhiteGridSection>
  );
}

function CreativeEngineSection() {
  return (
    <WhiteGridSection className="creative-engine-section">
      <div className="creative-engine-head">
        <V2Eyebrow>Creative growth engine</V2Eyebrow>
        <h2>
          One bold idea, shaped for <GradientText>brand, web, and social.</GradientText>
        </h2>
        <p>
          The work should not feel like separate tasks. Brand direction, web pages, campaign visuals, content, and
          reporting should move as one connected launch system.
        </p>
      </div>

      <div className="creative-engine">
        <div className="creative-engine__stage" aria-label="Brand, web, and campaign launch system">
          <div className="creative-engine__poster creative-engine__poster--brand">
            <img
              src="/media/growth/creative-growth-system.png"
              alt="Connected growth system linking content, learning, operations, and continuous improvement"
              loading="lazy"
              decoding="async"
            />
            <span>Growth</span>
          </div>
          <div className="creative-engine__poster creative-engine__poster--web">
            <img
              src="/media/growth/creative-targeted-growth.png"
              alt="Dart landing on the center of a target to represent focused growth"
              loading="lazy"
              decoding="async"
            />
            <span>Target</span>
          </div>
          <div className="creative-engine__poster creative-engine__poster--social">
            <img
              src="/media/growth/creative-connected-execution.png"
              alt="Blue center revealed within connected white puzzle pieces"
              loading="lazy"
              decoding="async"
            />
            <span>Connect</span>
          </div>
          <div className="creative-engine__signal creative-engine__signal--one">Hook</div>
          <div className="creative-engine__signal creative-engine__signal--two">Launch</div>
          <div className="creative-engine__signal creative-engine__signal--three">Learn</div>
        </div>

        <div className="creative-engine__steps">
          {CREATIVE_ENGINE_STEPS.map(([title, copy, Icon, color]) => (
            <button key={title} type="button" style={{ '--engine-color': color }} onClick={() => setRoute('gateway', { contact: true })}>
              <i>
                <Icon />
              </i>
              <strong>{title}</strong>
              <span>{copy}</span>
            </button>
          ))}
        </div>
      </div>
    </WhiteGridSection>
  );
}

function ClientWorkSection() {
  return (
    <WhiteGridSection className="client-work-section">
      <div className="client-work-head">
        <V2Eyebrow>11 · Case studies</V2Eyebrow>
        <h2>
          Strategy & <GradientText>Content Creation.</GradientText>
        </h2>
        <p>
          A look at three engagements where our team led marketing strategy and content creation—from brand positioning through
          ongoing execution.
        </p>
      </div>

      <div className="client-work-grid">
        {CLIENT_WORK.map((item) => (
          <article
            key={item.title}
            className="client-work-card"
            style={{
              '--client-color': item.color,
              '--client-bg': item.background || 'color-mix(in srgb, var(--client-color) 8%, #f7f9fc)',
            }}
          >
            <div className="client-work-card__media">
              <ImageRotator images={item.images} intervalMs={2000} className="client-work-card__rotator" />
            </div>
            <div className="client-work-card__body">
              <span>{item.category}</span>
              <h3>{item.title}</h3>
              <p>{item.summary}</p>
              <div>
                {item.tags.map((tag) => (
                  <b key={tag}>{tag}</b>
                ))}
              </div>
            </div>
          </article>
        ))}
      </div>
    </WhiteGridSection>
  );
}

function BrandResourcesSection() {
  return (
    <WhiteGridSection className="v2-brand-section">
      <div className="v2-section-head">
        <V2Eyebrow>Brand resources</V2Eyebrow>
        <h2>
          Build a brand system your team can <GradientText>actually use.</GradientText>
        </h2>
        <p>
          From strategy to guidelines, the output should help every page, post, proposal, and campaign feel consistent.
        </p>
      </div>
      <div className="v2-brand-resource">
        <div className="v2-brand-visual" aria-label="360 Digital Gurus brand resource stack">
          <div className="v2-brand-sheet v2-brand-sheet--back">
            <span>Colors</span>
            <div>
              <i style={{ background: '#0047AB' }} />
              <i style={{ background: '#38AECC' }} />
              <i style={{ background: '#E42613' }} />
              <i style={{ background: '#1D1D1B' }} />
            </div>
          </div>
          <div className="v2-brand-sheet v2-brand-sheet--middle">
            <span>Messaging</span>
            <strong>Clear, consistent, ready to publish.</strong>
          </div>
          <div className="v2-brand-sheet v2-brand-sheet--front">
            <img src="/360dg-icon.svg" alt="360 Digital Gurus" />
            <span>Brand resources</span>
          </div>
        </div>

        <div className="v2-brand-copy">
          <div className="v2-brand-grid">
            {BRAND_RESOURCES.map(([title, copy, Icon, color]) => (
              <button key={title} type="button" style={{ '--brand-resource-color': color }} onClick={() => setRoute('gateway', { contact: true })}>
                <i>
                  <Icon />
                </i>
                <span>
                  <strong>{title}</strong>
                  <em>{copy}</em>
                </span>
              </button>
            ))}
          </div>

          <button type="button" className="v2-brand-cta" onClick={() => setRoute('gateway', { contact: true })}>
            Discuss brand resources
            <FaArrowRight />
          </button>
        </div>
      </div>
    </WhiteGridSection>
  );
}

function OdooExperience() {
  return (
    <>
      <section className="odoo-proof">
        <div className="section-heading">
          <Kicker>odoo implementation</Kicker>
          <h2>A beautiful, easy-to-use ERP core that connects the work.</h2>
          <p>
            <OdooText>{`${publicCopy(ODOO_SERVICE.tagline)} We implement and support odoo with clean adoption, not just setup.`}</OdooText>
          </p>
          <div className="odoo-partner-badge">
            <img src="/odoo_logo.svg" alt="odoo Partner" />
            <span>Implementation support from a 360 Digital Gurus odoo partner team.</span>
          </div>
        </div>
        <div className="odoo-proof-grid">
          {[
            ['One app', 'All-in-one operations', '#38AECC'],
            ['Modular', 'Start focused, expand later', '#0047AB'],
            ['VAT / WPS', 'UAE-ready compliance', '#E42613'],
            ['Scalable', 'Built to integrate', '#714B67'],
          ].map(([label, title, color]) => (
            <article key={label} style={{ '--proof-color': color }}>
              <span>{label}</span>
              <strong>{title}</strong>
            </article>
          ))}
        </div>
      </section>
      <OdooApproachSection />
      <section className="odoo-canvas-section">
        <div className="section-heading">
          <Kicker>odoo module flow</Kicker>
          <h2>See how one operating system connects the work.</h2>
          <p>Tap a module to follow how data moves between CRM, sales, finance, inventory, projects, HR, and marketing.</p>
        </div>
        <OdooModuleMap />
      </section>
      <AfterLaunchSection />
    </>
  );
}

function AgencyExperience() {
  return (
    <>
      <DigitalMarketingSection />
      <CreativeEngineSection />
      <ClientWorkSection />
      <BrandResourcesSection />
    </>
  );
}

function OperationsExperience() {
  return (
    <section className="operations-lab">
      <div className="operations-lab__copy">
        <Kicker>Accounting delivery</Kicker>
        <h2>Monthly finance support without the messy handoffs.</h2>
        <p>
          Keep bookkeeping, VAT, payroll coordination, reconciliations, and management reporting moving in a clear rhythm
          so owners and managers can trust the numbers.
        </p>
      </div>
      <div className="operations-lab__visuals">
        <ImageRotator images={pathMedia.operations} className="operations-visual" />
      </div>
      <div className="operations-detail-grid">
        {OPERATIONS_DETAILS.map(([title, copy, Icon, color]) => (
          <article key={title} style={{ '--ops-detail-color': color }}>
            <i>
              <Icon />
            </i>
            <strong>
              <OdooText>{title}</OdooText>
            </strong>
            <span>
              <OdooText>{copy}</OdooText>
            </span>
          </article>
        ))}
      </div>
    </section>
  );
}

function HomeContact() {
  const interests = useMemo(() => allServices.map((service) => ({ id: service.id, label: publicCopy(service.name) })), []);
  const [selectedInterests, setSelectedInterests] = useState(['odoo']);
  const [teamSize, setTeamSize] = useState('50-200');
  const [submission, setSubmission] = useState({ status: 'idle', message: '' });
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    country: '',
    message: '',
    website: '',
  });
  const selectedInterestLabels = interests
    .filter((interest) => selectedInterests.includes(interest.id))
    .map((interest) => interest.label);

  const updateField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const toggleInterest = (interestId) => {
    setSelectedInterests((current) => {
      if (!current.includes(interestId)) return [...current, interestId];
      if (current.length === 1) return current;
      return current.filter((id) => id !== interestId);
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const serviceSummary = selectedInterestLabels.length ? selectedInterestLabels.join(', ') : 'Service inquiry';
    setSubmission({ status: 'sending', message: '' });
    try {
      await submitContact({ ...form, interests: serviceSummary, teamSize });
      setSubmission({ status: 'success', message: 'Thank you — your inquiry has been sent. We will contact you shortly.' });
      setForm({ firstName: '', lastName: '', email: '', company: '', country: '', message: '', website: '' });
    } catch (error) {
      setSubmission({ status: 'error', message: error.message });
    }
  };

  return (
    <section id="contact" className="home-contact">
      <div className="home-contact__copy">
        <Kicker>Speak with experts</Kicker>
        <h2>
          Ready to improve
          <span> how your business runs?</span>
        </h2>
        <p>
          Tell us what you want to automate, launch, or improve. We will help you map the right 360DG path and the next
          practical step.
        </p>
        <div className="contact-info-card">
          <div className="contact-info-grid">
            <a href={`tel:${PHONE_PRIMARY.replace(/\s/g, '')}`}>
              <span>
                <FaPhoneAlt />
                Call us
              </span>
              <strong>{PHONE_PRIMARY}</strong>
            </a>
            <a href={`mailto:${EMAIL_PRIMARY}`}>
              <span>
                <FaEnvelope />
                Email
              </span>
              <strong>{EMAIL_PRIMARY}</strong>
              <em>www.360digitalgurus.com</em>
            </a>
          </div>
          <div className="contact-social-links">
            <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" aria-label="Open 360 Digital Gurus on LinkedIn">
              <FaLinkedin />
              LinkedIn
            </a>
            <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" aria-label="Open 360 Digital Gurus on Facebook">
              <FaFacebook />
              Facebook
            </a>
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="Open 360 Digital Gurus on Instagram">
              <FaInstagram />
              Instagram
            </a>
          </div>
          <div className="contact-location">
            <strong>Dubai, UAE</strong>
            <span>Global delivery</span>
          </div>
        </div>
      </div>
      <form className="contact-form" onSubmit={handleSubmit}>
        <div className="contact-form__top">
          <span>Start project</span>
          <strong>Schedule a consultation</strong>
        </div>
        <div className="contact-form__row">
          <label>
            First name
            <input type="text" value={form.firstName} onChange={updateField('firstName')} autoComplete="given-name" required />
          </label>
          <label>
            Last name
            <input type="text" value={form.lastName} onChange={updateField('lastName')} autoComplete="family-name" required />
          </label>
        </div>
        <label>
          Work email
          <input type="email" value={form.email} onChange={updateField('email')} autoComplete="email" inputMode="email" required />
        </label>
        <div className="contact-form__row">
          <label>
            Company
            <input type="text" value={form.company} onChange={updateField('company')} autoComplete="organization" required />
          </label>
          <label>
            Country
            <input
              type="text"
              value={form.country}
              onChange={updateField('country')}
              list="contact-country-options"
              autoComplete="country-name"
              required
            />
          </label>
        </div>
        <datalist id="contact-country-options">
          {COUNTRY_OPTIONS.map((country) => (
            <option key={country} value={country} />
          ))}
        </datalist>
        <div className="contact-choice contact-choice--multi contact-choice--services">
          <span>I am interested in</span>
          <div>
            {interests.map((interest) => (
              <button
                key={interest.id}
                type="button"
                className={`${selectedInterests.includes(interest.id) ? 'is-active' : ''}${interest.id === 'odoo' ? ' is-odoo-service' : ''}`.trim()}
                style={{ '--chip-color': serviceById[interest.id]?.color || '#0047AB' }}
                onClick={() => toggleInterest(interest.id)}
                aria-label={interest.label}
                aria-pressed={selectedInterests.includes(interest.id)}
              >
                {interest.id === 'odoo' ? (
                  <>
                    <OdooName />
                    <span className="contact-choice__odoo-copy">{interest.label.replace(/^odoo\s+/i, '')}</span>
                  </>
                ) : (
                  <OdooText>{interest.label}</OdooText>
                )}
              </button>
            ))}
          </div>
        </div>
        <div className="contact-choice">
          <span>Team size</span>
          <div>
            {['1-50', '50-200', '200-1k', '1k+'].map((size) => (
              <button
                key={size}
                type="button"
                className={teamSize === size ? 'is-active' : ''}
                style={{ '--chip-color': '#0047AB' }}
                onClick={() => setTeamSize(size)}
              >
                {size}
              </button>
            ))}
          </div>
        </div>
        <label>
          Project details
          <textarea
            rows="3"
            value={form.message}
            onChange={updateField('message')}
            placeholder="Tell us what you want to automate, launch, or improve."
          />
        </label>
        <input className="contact-form__honeypot" type="text" name="website" value={form.website} onChange={updateField('website')} tabIndex="-1" autoComplete="off" aria-hidden="true" />
        <button type="submit" disabled={submission.status === 'sending'}>
          {submission.status === 'sending' ? 'Sending...' : 'Send & schedule discovery'}
          <FaArrowRight />
        </button>
        {submission.message && (
          <p className={`contact-form__sent${submission.status === 'error' ? ' contact-form__sent--error' : ''}`} role="status">
            {submission.message}
          </p>
        )}
        <p className="contact-form__privacy">We will use your details only to respond to this inquiry.</p>
      </form>
    </section>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div className="site-footer__brand">
        <img src="/360dg-logo-white.svg" alt="360 Digital Gurus" />
        <p>
          <OdooText>Empowering business transformation through odoo ERP, custom apps, AI automation, brand growth, web, marketing, and accounting support.</OdooText>
        </p>
      </div>

      <nav className="site-footer__links" aria-label="Footer navigation">
        <span>Paths</span>
        <button onClick={() => setRoute('odoo')}>
          <OdooText>{paths.odoo.label}</OdooText>
        </button>
        <button onClick={() => setRoute('agency')}>{paths.agency.nav}</button>
        <button onClick={() => setRoute('operations')}>{paths.operations.nav}</button>
        <button onClick={() => setRoute('gateway', { contact: true })}>Contact</button>
      </nav>

      <div className="site-footer__contact">
        <span>Dubai, UAE / Global delivery</span>
        <a href={`mailto:${EMAIL_PRIMARY}`}>{EMAIL_PRIMARY}</a>
        <a href={`tel:${PHONE_PRIMARY.replace(/\s/g, '')}`}>{PHONE_PRIMARY}</a>
        <div className="site-footer__socials">
          <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" aria-label="Open LinkedIn">
            <FaLinkedin />
          </a>
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="Open Instagram">
            <FaInstagram />
          </a>
          <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" aria-label="Open Facebook">
            <FaFacebook />
          </a>
          <button onClick={() => window.open(WHATSAPP_URL, '_blank', 'noopener,noreferrer')}>
            <FaWhatsapp />
            WhatsApp
          </button>
        </div>
        <a className="site-footer__powered" href={COMPANY.poweredBy.url} target="_blank" rel="noopener noreferrer">
          Powered by <strong>{COMPANY.poweredBy.label}</strong>
        </a>
      </div>
    </footer>
  );
}

function FloatingWhatsapp() {
  return (
    <a className="floating-whatsapp" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" aria-label="Chat with 360 Digital Gurus on WhatsApp">
      <span className="floating-whatsapp__note">Hey there</span>
      <span className="floating-whatsapp__icon" aria-hidden="true">
        <FaWhatsapp />
      </span>
    </a>
  );
}

export default function Homepage() {
  const route = useRoute();
  usePageSeo(route);

  let view = <HomePage />;
  if (route === 'odoo') view = <PathPage path={paths.odoo} />;
  if (route === 'agency') view = <PathPage path={paths.agency} />;
  if (route === 'operations') view = <PathPage path={paths.operations} />;

  return (
    <>
      <CurtainLoader route={route} />
      <ScrollLifeEffects route={route} />
      <Navigation route={route} />
      {view}
      <FloatingWhatsapp />
      <Footer />
    </>
  );
}
